import { Modifier } from '../modifier';
import type { Damage } from '../damage.type';
import { addModifier, dealDamage, healthRemove } from '../player.type';
import type { Technique } from '../techniques.type';
import { techniqueHistory } from '../../../store';
import type { Die } from '../die.type';
import type { Player } from '../player.type';
import { DieFace } from '../face.type';
import {
  ModifierPriority,
  ModifierTags,
  StatusEffect,
  type ModifierParams,
} from '../modifier.type';
import { SoundType } from '../../components/audio.svelte';
import { StatusEffectRegistry } from '../status-effects';

export class Grappled extends Modifier {
  static readonly baseTitle = 'Grappled';
  static readonly baseText =
    'Gives techniques "Struggle" and "Flail" to remove stacks. Take damage equal to double the stacks at the end of turn.';
  static readonly baseIcon = 'grappled';
  static readonly baseTags = [ModifierTags.Bane];
  static readonly basePriority = ModifierPriority.Middle;

  // Instance properties that can change
  title = Grappled.baseTitle;
  text = Grappled.baseText;
  icon = Grappled.baseIcon;
  tags: Array<ModifierTags> = [...Grappled.baseTags];
  priority = Grappled.basePriority;

  getTechniques(techniques: Technique[]): Technique[] {
    return [
      {
        title: 'Struggle',
        text: 'Take 1 Pure Damage. Gain 1 Weakness. Remove 1 stack of Grappled.',
        tags: ['required'],
        ingredients: [],
        audio: SoundType.None,
        requirements: [
          {
            text: 'Has 1 Health.',
            icon: 'blank',
            test: (source: Player, target: Player) => {
              return source.healthCurrent > 1;
            },
          },
        ],
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          healthRemove({ target: source, source: target, value: 1, dice: [] });
          this.removeStacks(1);
        },
      },
      {
        title: 'Flail',
        text: 'Remove 1 stack of Grappled.',
        tags: ['required'],
        ingredients: [DieFace.Blank],
        audio: SoundType.None,
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          this.removeStacks(1);
        },
      },
      {
        title: 'Thrash',
        text: 'Gain 1 stack of Vulnerable. Remove 1 stack of Grappled.',
        tags: ['required'],
        ingredients: [DieFace.Blank],
        audio: SoundType.None,
        activate: (target: Player, source: Player, dice: Array<Die>) => {
          this.removeStacks(1);
          addModifier(source, StatusEffect.Vulnerable, 1);
        },
      },
    ];
  }

  update(stacks: number, params?: ModifierParams) {
    super.update(stacks, params);
    this.delay = true;
  }

  endTurn() {
    healthRemove({ target: this.owner, source: this.owner, value: this.stacks * 2, dice: [] });
    this.removeStacks(this.stacks);
  }
}

// Register the status effect
StatusEffectRegistry.register(StatusEffect.Grappled, Grappled);
